import type { apiTypes } from '@cmg/common';
import { Form, FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

import usePrevious from '../../../../../../common/util/usePrevious';
import { ServerErrorAlert } from '../../../../../../design-system/alert/ServerErrorAlert';
import { PageGridSection } from '../../../../../../design-system/body-sections/PageGridSection';
import { PageGridSectionActions } from '../../../../../../design-system/body-sections/PageGridSectionActions';
import type {
  AzureAdConfigurationUpdate,
  AzureAdOpenIdMetadata,
} from '../../../../../../types/domain/identity-provider/configurations/azureAd';
import type { IdentityProviderAzure } from '../../../../../../types/domain/identity-provider/identityProvider';
import { ProviderDetailsSectionEdit } from './ProviderDetailsSectionEdit';
import { ProviderDetailsSectionReadonly } from './ProviderDetailsSectionReadonly';
import { AzureAdConfigurationUpdateSchema } from './utils';

export type Props = Readonly<{
  readonly canEdit: boolean;
  readonly provider: IdentityProviderAzure | null;
  readonly metadata: AzureAdOpenIdMetadata | null;
  readonly error: apiTypes.GenericServerError | null;
  readonly isSubmitting: boolean;
  readonly onSubmit: (data: AzureAdConfigurationUpdate) => void;
  readonly onReset: () => void;
}>;

export const ProviderDetailsSection: FC<Props> = ({
  canEdit,
  provider,
  metadata,
  error,
  isSubmitting,
  onSubmit,
  onReset,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const formik = useFormik<AzureAdConfigurationUpdate>({
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    validationSchema: AzureAdConfigurationUpdateSchema,
    initialValues: {
      enabled: provider?.enabled ?? false,
      name: provider?.name ?? '',
      configuration: {
        clientId: provider?.configuration.clientId ?? '',
        tenantId: provider?.configuration.tenantId ?? '',
        authorizedGroupId: provider?.configuration.authorizedGroupId ?? '',
      },
    },
    onSubmit: val => {
      onSubmit({
        ...val,
        configuration: {
          ...val.configuration,
          authorizedGroupId: val.configuration.authorizedGroupId || null, // the end point requires null if no value is provided
        },
      });
    },
  });

  const { resetForm } = formik;
  const previousIsEditing = usePrevious(isEditing);
  useEffect(() => {
    if (previousIsEditing && !isEditing) {
      resetForm();
      onReset();
    }
  }, [isEditing, onReset, previousIsEditing, resetForm]);

  return (
    <FormikProvider value={formik}>
      <PageGridSection
        size="medium"
        title="Provider Details"
        FormComponent={Form}
        formComponentProps={{ noValidate: true }}
        actions={
          canEdit ? (
            <PageGridSectionActions
              isSubmitting={isSubmitting}
              isEditing={isEditing}
              onEdit={() => setIsEditing(true)}
              onCancel={() => setIsEditing(false)}
              canEdit={canEdit}
            />
          ) : undefined
        }
        error={error && <ServerErrorAlert title="Error" error={error} />}
        content={
          isEditing ? (
            <ProviderDetailsSectionEdit metadata={metadata} />
          ) : (
            <ProviderDetailsSectionReadonly provider={provider} metadata={metadata} />
          )
        }
      />
    </FormikProvider>
  );
};
